import React from "react";
import { DialogProps, ChannelInfo } from "../../types/dialog-types";
import { tagSelectStyles as s } from "./tagSelectDialogStyles";
import { getTigerResource } from "../../utils/tigerResource";
import LangDateInput from "../LangDateInput";
import { tigerSecondsToDate, TIGER_TO_UNIX_OFFSET_SECS, validateEndAfterStart } from "../../utils/tigerTime";

const FIVE_DAYS_SECS = 5 * 86400;

interface HistTrendDiagSelectTagDialogProps extends DialogProps {
  tags?: ChannelInfo[];
  /** Diagnostic message time in Tiger seconds; used to prefill the date range. */
  msgTime?: number;
}

/** Tag-selection dialog for the diagnostics "Tools → Historical Trend Graph"
 *  action: a start/end date range plus the message's tags. Themed to match the
 *  Graph Channel Selection dialog; labels via getTigerResource. */
export function HistTrendDiagSelectTagDialog({
  open,
  onClose,
  onApply,
  tags = [],
  msgTime,
}: HistTrendDiagSelectTagDialogProps) {
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const [startDate, setStartDate] = React.useState<string>("");
  const [endDate, setEndDate] = React.useState<string>("");

  // Default range: 5 days either side of the message time, end capped at today.
  React.useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    if (msgTime == null) return;
    const toDateStr = (tigerSecs: number) => tigerSecondsToDate(tigerSecs).toISOString().slice(0, 10);
    const nowTiger = Math.floor(Date.now() / 1000) + TIGER_TO_UNIX_OFFSET_SECS;
    setStartDate(toDateStr(msgTime - FIVE_DAYS_SECS));
    setEndDate(toDateStr(Math.min(msgTime + FIVE_DAYS_SECS, nowTiger)));
  }, [open, msgTime]);

  const toggleRow = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleOk = () => {
    const chosen = tags.filter((_, idx) => selected.has(idx));
    if (chosen.length > 0) {
      const rangeErr = validateEndAfterStart(startDate, endDate);
      if (rangeErr) {
        alert(rangeErr);
        return;
      }
      onApply?.({ tags: chosen, startDate, endDate });
    }
    onClose();
  };

  if (!open) return null;
  const title = getTigerResource("TIGER_GRAPH_CHANNEL_HIST_GRAPH_TITLE");

  return (
    <div style={s.overlay}>
      <div style={s.dialog} role="dialog" aria-modal="true" aria-label={title}>
        <div style={s.header}>
          <span style={s.title}>{title}</span>
          <button style={s.closeBtn} onClick={onClose}>
            &#x2715;
          </button>
        </div>

        <div style={s.body}>
          <div style={s.card}>
            <div style={s.fieldGroup}>
              <div style={s.field}>
                <label style={s.fieldLabel}>{getTigerResource("TIGER_RES_START_DATE")}</label>
                <LangDateInput style={s.input} value={startDate} onChange={setStartDate} />
              </div>
              <div style={s.field}>
                <label style={s.fieldLabel}>{getTigerResource("TIGER_RES_END_DATE")}</label>
                <LangDateInput style={s.input} value={endDate} onChange={setEndDate} />
              </div>
            </div>
          </div>

          <div style={s.section}>
            <div style={s.tableWrap}>
              <div className="list-control" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 180 }}>{getTigerResource("TIGER_EXPORT_CHANNEL_NAME")}</th>
                      <th style={{ width: 300 }}>{getTigerResource("TIGER_EXPORT_CHANNEL_DESCR")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tags.map((item, idx) => (
                      <tr
                        key={idx}
                        className={selected.has(idx) ? "selected" : ""}
                        onClick={() => toggleRow(idx)}
                      >
                        <td>{item.tag}</td>
                        <td>{item.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div style={s.footer}>
          <button
            style={{ ...s.btnPrimary, opacity: selected.size === 0 ? 0.4 : 1 }}
            onClick={handleOk}
            disabled={selected.size === 0}
          >
            {getTigerResource("TIGER_RES_OK")}
          </button>
          <button style={s.btnSecondary} onClick={onClose}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
}
